import { useCallback, useEffect, useState } from "react";
import { fmtMYT } from "@/lib/myt";
import { CheckCircle2, Coins, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

const STATUS_STYLES = {
  pending: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/30",
  approved: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
  cancelled: "text-[#94A3B8] bg-[#94A3B8]/10 border-[#94A3B8]/25",
  expired: "text-[#94A3B8] bg-[#94A3B8]/10 border-[#94A3B8]/25",
};

const fmtMoney = (currency, amount) =>
  `${currency} ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminCoinOrders() {
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback((status) => {
    setOrders(null);
    api.get("/admin/coin-orders", { params: { status } })
      .then((res) => setOrders(res.data.orders || []))
      .catch(() => setOrders([]));
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const approve = async (o) => {
    if (busyId) return;
    if (!window.confirm(`Approve order ${o.reference} and credit ${o.coin_quantity} COINS to ${o.member_name || o.member_username}? Only do this after the payment has actually arrived.`)) return;
    setBusyId(o.id);
    try {
      const res = await api.post(`/admin/coin-orders/${o.id}/approve`);
      toast.success(`Approved — ${o.coin_quantity} COINS credited (balance ${(res.data.balance_minor / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}).`);
      load(tab);
    } catch (err) {
      toast.error(formatApiError(err));
      load(tab);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (o) => {
    if (busyId) return;
    const reason = window.prompt(`Reject order ${o.reference}? Optional reason for the member:`, "");
    if (reason === null) return;
    setBusyId(o.id);
    try {
      await api.post(`/admin/coin-orders/${o.id}/reject`, { reason });
      toast.success("Order rejected — no coins were credited.");
      load(tab);
    } catch (err) {
      toast.error(formatApiError(err));
      load(tab);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-coin-orders-page">
      <div>
        <p className="eyebrow-label mb-2">Coin Top-Up Approvals</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Coin Top-Up Orders</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          Approve a top-up ONLY after the member's payment has actually arrived. Approval credits the exact stored Coin quantity once — the screenshot is evidence only and never auto-approves.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="coin-orders-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs font-mono tracking-widest uppercase rounded-full px-4 py-2 border transition-colors ${
              tab === t.key ? "text-[#050506] bg-[#E5C158] border-[#E5C158]" : "text-[#94A3B8] border-[#222530] hover:border-[#D4AF37]/40"
            }`}
            data-testid={`coin-orders-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="card-lux overflow-hidden" data-testid="coin-orders-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-coin-orders-table">
            <thead>
              <tr className="border-b border-[#222530] text-left">
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Reference / Member</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Coins</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Amount</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden md:table-cell">Rate</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide hidden lg:table-cell">Quote Expires</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Status</th>
                <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222530]">
              {!orders ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-[#64748B]">Loading...</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-[#64748B]" data-testid="coin-orders-empty">No orders in this view.</td></tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id} className="hover:bg-[#0B0C10]/60 transition-colors align-top" data-testid={`admin-coin-order-${o.reference}`}>
                    <td className="px-5 py-4">
                      <p className="font-mono text-white">{o.reference}</p>
                      <p className="text-xs text-[#94A3B8] mt-0.5">{o.member_name || "—"}</p>
                      <p className="text-[10px] font-mono text-[#64748B]">{o.member_uid} · {o.member_username}</p>
                    </td>
                    <td className="px-5 py-4 font-mono text-right text-white whitespace-nowrap">{o.coin_quantity}</td>
                    <td className="px-5 py-4 font-mono text-right text-[#E5C158] whitespace-nowrap" data-testid={`coin-order-amount-${o.reference}`}>{fmtMoney(o.currency, o.converted_amount)}</td>
                    <td className="px-5 py-4 font-mono text-[#94A3B8] hidden md:table-cell whitespace-nowrap">1 USD = {Number(o.exchange_rate).toLocaleString("en-US", { maximumFractionDigits: 4 })}</td>
                    <td className="px-5 py-4 text-[#94A3B8] hidden lg:table-cell whitespace-nowrap text-xs">{o.quote_expires_at ? fmtMYT(o.quote_expires_at) : "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${STATUS_STYLES[o.status] || STATUS_STYLES.pending}`} data-testid={`admin-coin-order-status-${o.reference}`}>
                        {o.status}
                      </span>
                      {o.status === "rejected" && o.reject_reason && (
                        <p className="text-[10px] text-[#64748B] mt-1 max-w-[160px]">{o.reject_reason}</p>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      {o.status === "pending" ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => approve(o)}
                            disabled={busyId === o.id}
                            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-400/40 rounded-lg px-3 py-1.5 hover:bg-emerald-400/10 transition-colors disabled:opacity-50"
                            data-testid={`coin-order-approve-${o.reference}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => reject(o)}
                            disabled={busyId === o.id}
                            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-red-400 border border-red-400/40 rounded-lg px-3 py-1.5 hover:bg-red-400/10 transition-colors disabled:opacity-50"
                            data-testid={`coin-order-reject-${o.reference}`}
                          >
                            <XCircle className="h-3.5 w-3.5" /> Reject
                          </button>
                        </div>
                      ) : o.status === "approved" ? (
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-emerald-400"><Coins className="h-3.5 w-3.5" /> Credited</span>
                      ) : (
                        <span className="text-[11px] font-mono text-[#64748B]">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
